import { useState, useEffect, useCallback, useRef } from "react";
import { TrendingUp, Info, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useToast } from "../../components/ui/toast";

import { formatCurrency } from "../../lib/utils";
import { FinancingSettings } from "../../types/financing";
import {
  createDefaultFinancingSettings,
  ltcPresets,
  interestRatePresets,
} from "../../data/financingDefaults";
import { buildDebtSchedule, calculateFinancingAmounts } from "../../lib/debt";
import { debounce } from "@/utils/useDebounce";
import { getProjecById } from "@/lib/datasource/supabase";
import { patchProjectKpis } from "@/lib/datasource";
import { ICurrency } from "@/types";

function countRoomsFromMix(roomMix: any): number {
  if (!roomMix) return 0;

  if (Array.isArray(roomMix)) {
    return roomMix.reduce((total, item) => {
      const rooms =
        Number(item.rooms) ||
        Number(item.count) ||
        Number(item.roomCount) ||
        Number(item.quantity) ||
        0;
      return total + rooms;
    }, 0);
  }

  // Если это объект с полем types
  if (roomMix.types && Array.isArray(roomMix.types)) {
    return roomMix.types.reduce((total, item) => {
      const rooms =
        Number(item.rooms) ||
        Number(item.count) ||
        Number(item.roomCount) ||
        Number(item.quantity) ||
        0;
      return total + rooms;
    }, 0);
  }

  // Если это объект с полем items
  if (roomMix.items && Array.isArray(roomMix.items)) {
    return roomMix.items.reduce((total, item) => {
      const rooms =
        Number(item.rooms) ||
        Number(item.count) ||
        Number(item.roomCount) ||
        Number(item.quantity) ||
        0;
      return total + rooms;
    }, 0);
  }

  // Если есть поле total
  if (roomMix.total && typeof roomMix.total === "number") {
    return roomMix.total;
  }

  return 0;
}

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
  <div className={`rounded bg-slate-200 animate-pulse ${className}`} />
);

const FinancingStructureSkeleton = () => {
  return (
    <div className="space-y-6 animate-in fade-in-0 duration-300">
      <div className="space-y-3">
        <SkeletonBlock className="h-7 w-1/3" />
        <SkeletonBlock className="h-14 w-full" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <SkeletonBlock className="h-24 w-full rounded-xl" />
        <SkeletonBlock className="h-24 w-full rounded-xl" />
        <SkeletonBlock className="h-24 w-full rounded-xl" />
      </div>
    </div>
  );
};

function QuickButton({
  value,
  active,
  onClick,
  suffix = "",
}: {
  value: number;
  active: boolean;
  onClick: () => void;
  suffix?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded text-sm font-medium transition-all ${
        active
          ? "bg-primary-blue text-white shadow-sm"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {value}
      {suffix}
    </button>
  );
}

interface FinancingStructureProps {
  dealId: string;
  onSaved?: () => void;
}

export default function FinancingStructure({
  dealId,
  onSaved,
}: FinancingStructureProps) {
  const { toast } = useToast();

  const [financingSettings, setFinancingSettings] =
    useState<FinancingSettings | null>(null);
  const [originalSettings, setOriginalSettings] =
    useState<FinancingSettings | null>(null);
  const [projectData, setProjectData] = useState<{
    currency: ICurrency;
    grandTotal: number;
    totalInvestment: number;
    gfaSqm: number;
    roomMix: any;
  } | null>(null);

  const [saveState, setSaveState] = useState<"idle" | "saving" | "success">(
    "idle",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [animatedFields, setAnimatedFields] = useState<Set<string>>(new Set());

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const loadAndMigrateFinancing = (kpisFinancing: any): FinancingSettings => {
    const defaults = createDefaultFinancingSettings();
    if (!kpisFinancing || typeof kpisFinancing !== "object") return defaults;

    if (kpisFinancing.ltcPct !== undefined) {
      return { ...defaults, ...kpisFinancing };
    }

    return {
      ...defaults,
      ltcPct: kpisFinancing.debtToEquityRatio || defaults.ltcPct,
      interestRatePct: kpisFinancing.interestRate || defaults.interestRatePct,
      loanTermYears: kpisFinancing.loanTermYears || defaults.loanTermYears,
      amortYears: kpisFinancing.amortYears || defaults.amortYears,
      taxRateOnEBT: kpisFinancing.taxRateOnEBT || defaults.taxRateOnEBT,
      // ЗАМОРОЖЕНО: Поле investmentOrder временно не используется
      // TODO: Реализовать логику очередности инвестиций когда будет готова P&L модель
      investmentOrder: defaults.investmentOrder,
    };
  };

  useEffect(() => {
    let isMounted = true;

    if (!dealId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const row = await getProjecById(dealId);

        if (isMounted && row) {
          const kpis = row.kpis || {};
          const financingData = loadAndMigrateFinancing(kpis.financing);

          setFinancingSettings(financingData);
          setOriginalSettings(financingData);
          setProjectData({
            currency: row.currency || "USD",
            grandTotal: kpis.budget?.grandTotal || 0,
            totalInvestment: kpis?.totalInvestment || 0,
            gfaSqm: kpis.gfaSqm || 0,
            roomMix: kpis.roomMix || [],
          });
        }
      } catch (error) {
        console.error("[FinancingStructure] Load error:", error);
        toast.error("Failed to load project data");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [dealId]);

  const animateFields = useCallback((fields: string[]) => {
    setAnimatedFields(new Set(fields));
    setTimeout(() => setAnimatedFields(new Set()), 1000);
  }, []);

  const debouncedRecalculate = useCallback(
    debounce(() => {
      animateFields(["financing-structure", "debt-service"]);
    }, 200),
    [animateFields],
  );

  const handleFieldChange = (
    field: keyof FinancingSettings,
    value: number | string,
  ) => {
    if (!financingSettings) return;
    const newSettings = { ...financingSettings, [field]: value };
    setFinancingSettings(newSettings);
    debouncedRecalculate();
  };

  const handleSave = async () => {
    if (!financingSettings) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setSaveState("saving");

    try {
      // ЗАМОРОЖЕНО: Убираем investmentOrder из сохраняемых данных, так как функционал неактивен
      const settingsToSave = {
        ...financingSettings,
        investmentOrder: undefined, // Не сохраняем это поле пока функционал не готов
      };

      const patch = { financing: settingsToSave };

      await patchProjectKpis(dealId, patch);

      setOriginalSettings(financingSettings);
      setSaveState("success");
      toast.success("Financing structure saved");

      if (onSaved) {
        onSaved();
      }

      timerRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 2000);
    } catch (error) {
      console.error("[FinancingStructure] Save error:", error);
      setSaveState("idle");
      toast.error("Failed to save. Check console.");
    }
  };

  const handleCancel = () => {
    if (originalSettings) {
      setFinancingSettings(originalSettings);
      toast.info("Changes discarded");
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (isLoading || !financingSettings || !projectData) {
    return <FinancingStructureSkeleton />;
  }

  const rooms = countRoomsFromMix(projectData.roomMix);

  const projectCost = projectData.totalInvestment;
  const currency = projectData.currency;

  if (projectCost === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Financing Structure
          </h3>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800 font-medium">
                Complete Investment Budget to configure financing structure.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { loanAmount, equityRequired } = calculateFinancingAmounts(
    projectCost,
    financingSettings.ltcPct,
  );

  const debtSchedule = buildDebtSchedule(financingSettings, projectCost);

  const getAnimationClass = (field: string) =>
    animatedFields.has(field)
      ? "animate-pulse bg-brand-50 transition-all duration-500"
      : "";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Financing Structure
        </h3>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="text-blue-800 text-sm font-medium">
            Configure debt financing, loan terms, and tax assumptions.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">
            Total Project Cost
          </div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(projectCost, currency)}
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Cost per Room</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(rooms > 0 ? projectCost / rooms : 0, currency)}
          </div>
          <div className="text-xs text-slate-400 mt-1">Rooms: {rooms}</div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Cost per sqm</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(
              projectData.gfaSqm > 0 ? projectCost / projectData.gfaSqm : 0,
              currency,
            )}
          </div>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">Financing Structure</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <label className="block text-sm font-medium text-slate-700">
                LTC Ratio (Loan-to-Cost)
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Percentage of cost financed with debt.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center space-x-3 mb-3">
              <div className="relative">
                <input
                  type="number"
                  value={financingSettings.ltcPct || ""}
                  onChange={(e) =>
                    handleFieldChange("ltcPct", Number(e.target.value) || 0)
                  }
                  className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-500"
                  min="0"
                  max="100"
                />
                <span className="absolute right-3 top-2 text-slate-500 text-sm">
                  %
                </span>
              </div>
              <div className="flex space-x-2">
                {ltcPresets.map((pct) => (
                  <QuickButton
                    key={pct}
                    value={pct}
                    active={financingSettings.ltcPct === pct}
                    onClick={() => handleFieldChange("ltcPct", pct)}
                    suffix={pct === 0 ? "" : "%"}
                  />
                ))}
              </div>
            </div>
          </div>

          <div
            className={`border border-slate-200 rounded-lg p-4 bg-slate-50 ${getAnimationClass("financing-structure")}`}
          >
            <h4 className="font-medium text-slate-900 mb-3">
              Calculated Structure
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Loan Amount:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(loanAmount, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Equity Required:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(equityRequired, currency)}
                </span>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex h-4 rounded-full overflow-hidden bg-slate-200">
                {financingSettings.ltcPct > 0 && (
                  <div
                    className="bg-red-400 flex items-center justify-center text-[10px] text-white"
                    style={{ width: `${financingSettings.ltcPct}%` }}
                  >
                    {financingSettings.ltcPct > 15 && "Debt"}
                  </div>
                )}
                {100 - financingSettings.ltcPct > 0 && (
                  <div
                    className="bg-green-400 flex items-center justify-center text-[10px] text-white"
                    style={{ width: `${100 - financingSettings.ltcPct}%` }}
                  >
                    {100 - financingSettings.ltcPct > 15 && "Equity"}
                  </div>
                )}
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>Debt {financingSettings.ltcPct}%</span>
                <span>Equity {100 - financingSettings.ltcPct}%</span>
              </div>
            </div>
          </div>

          {/* ЗАМОРОЖЕНО: Секция Investment Order удалена из UI */}
          {/* TODO: Восстановить когда будет готова P&L модель и логика очередности инвестиций */}
        </CardContent>
      </Card>

      {financingSettings.ltcPct > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Loan Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Interest Rate
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input
                        type="number"
                        value={financingSettings.interestRatePct || ""}
                        onChange={(e) =>
                          handleFieldChange(
                            "interestRatePct",
                            Number(e.target.value) || 0,
                          )
                        }
                        className="w-24 px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        step="0.1"
                      />
                      <span className="absolute right-3 top-2.5 text-slate-500 text-sm">
                        %
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      {interestRatePresets.map((rate) => (
                        <QuickButton
                          key={rate}
                          value={rate}
                          active={financingSettings.interestRatePct === rate}
                          onClick={() =>
                            handleFieldChange("interestRatePct", rate)
                          }
                          suffix="%"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Amortization (Years)
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input
                        type="number"
                        value={financingSettings.amortYears || ""}
                        onChange={(e) =>
                          handleFieldChange(
                            "amortYears",
                            Number(e.target.value) || 0,
                          )
                        }
                        className="w-24 px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        min="1"
                        max="30"
                      />
                    </div>
                    <div className="flex space-x-2">
                      {[10, 15, 20, 25].map((years) => (
                        <QuickButton
                          key={years}
                          value={years}
                          active={financingSettings.amortYears === years}
                          onClick={() => handleFieldChange("amortYears", years)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">Tax Assumptions</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tax Rate on EBT
            </label>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <input
                  type="number"
                  value={financingSettings.taxRateOnEBT || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "taxRateOnEBT",
                      Number(e.target.value) || 0,
                    )
                  }
                  className="w-20 px-3 py-2 border border-slate-300 rounded-md"
                  step="0.1"
                />
                <span className="absolute right-3 top-2 text-slate-500 text-sm">
                  %
                </span>
              </div>
              <div className="flex space-x-2">
                {[20, 25, 30, 35].map((rate) => (
                  <QuickButton
                    key={rate}
                    value={rate}
                    active={financingSettings.taxRateOnEBT === rate}
                    onClick={() => handleFieldChange("taxRateOnEBT", rate)}
                    suffix="%"
                  />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {financingSettings.ltcPct > 0 && (
        <Card
          className={`border-slate-200 ${getAnimationClass("debt-service")}`}
        >
          <CardHeader>
            <CardTitle className="text-base">Summary & Debt Service</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
                <div className="text-sm font-medium text-navy-600">
                  Monthly Payment
                </div>
                <div className="mt-2 text-2xl font-semibold text-navy-900">
                  {formatCurrency(debtSchedule?.monthlyPayment, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
                <div className="text-sm font-medium text-navy-600">
                  Annual Service
                </div>
                <div className="mt-2 text-2xl font-semibold text-navy-900">
                  {formatCurrency(debtSchedule.annualDebtService, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
                <div className="text-sm font-medium text-navy-600">LTC</div>
                <div className="mt-2 text-2xl font-semibold text-navy-900">
                  {financingSettings.ltcPct.toFixed(1)}%
                </div>
              </div>
              <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
                <div className="text-sm font-medium text-navy-600">Equity</div>
                <div className="mt-2 text-2xl font-semibold text-navy-900">
                  {formatCurrency(equityRequired, currency)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center space-x-3 pt-4 justify-end">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={saveState === "saving"}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saveState === "saving"}
          className={`${
            saveState === "success"
              ? "bg-green-600 hover:bg-green-700"
              : "bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_40%)]"
          } text-white flex items-center space-x-2`}
        >
          {saveState === "saving" && (
            <TrendingUp className="h-4 w-4 animate-spin" />
          )}
          {saveState === "success" && <TrendingUp className="h-4 w-4" />}
          <span>
            {saveState === "saving"
              ? "Saving..."
              : saveState === "success"
                ? "Saved ✔"
                : "Save Financing"}
          </span>
        </Button>
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-500">
          Recalculated • {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
